import React, { useState, useEffect } from 'react';
import { Clock, CalendarDays, Trophy, Award, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const formatDuration = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const UsageDetail = ({ apiClient, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/admin/usage-report/${userId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="py-4 text-center text-sm text-gray-400">Cargando detalle...</div>;
  if (!data) return <div className="py-4 text-center text-sm text-gray-400">Sin datos</div>;

  const maxMin = Math.max(1, ...data.days.map(d => d.minutes));
  const maxPts = Math.max(1, ...data.days.map(d => d.points));

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-2" data-testid={`usage-detail-${userId}`}>
      <div className="grid grid-cols-2 gap-4">
        {/* Minutes per day */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><Clock size={12} /> Minutos por día (30d)</p>
          <div className="flex items-end gap-0.5 h-24">
            {data.days.map((d, i) => (
              <div key={i} className="flex-1 bg-black rounded-sm hover:bg-gray-700 transition-colors"
                style={{ height: `${(d.minutes / maxMin) * 100}%`, minHeight: d.minutes > 0 ? '2px' : '0' }}
                title={`${d.date}: ${d.minutes} min`} />
            ))}
          </div>
        </div>
        {/* Points per day */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><Trophy size={12} /> Puntos por día (30d)</p>
          <div className="flex items-end gap-0.5 h-24">
            {data.days.map((d, i) => (
              <div key={i} className="flex-1 bg-amber-500 rounded-sm hover:bg-amber-600 transition-colors"
                style={{ height: `${(d.points / maxPts) * 100}%`, minHeight: d.points > 0 ? '2px' : '0' }}
                title={`${d.date}: ${d.points} pts`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const UserUsageAdmin = ({ apiClient }) => {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState('minutes');

  const fetchReport = () => {
    setLoading(true);
    apiClient.get('/admin/usage-report')
      .then(r => setReport(r.data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchReport(); }, []);

  const sorted = [...report].sort((a, b) => {
    if (sortBy === 'minutes') return b.total_minutes - a.total_minutes;
    if (sortBy === 'days') return b.active_days - a.active_days;
    if (sortBy === 'points') return b.total_points - a.total_points;
    return (a.full_name || '').localeCompare(b.full_name || '');
  });

  return (
    <div data-testid="user-usage-admin-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-black">Uso de la aplicación</h2>
          <p className="text-sm text-gray-500">Tiempo y actividad de los usuarios en los últimos 30 días</p>
        </div>
        <button onClick={fetchReport} data-testid="usage-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      {/* Sort controls */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[
          { k: 'minutes', l: 'Tiempo' },
          { k: 'days', l: 'Días activos' },
          { k: 'points', l: 'Puntos' },
          { k: 'name', l: 'Nombre' },
        ].map(o => (
          <button key={o.k} onClick={() => setSortBy(o.k)} data-testid={`usage-sort-${o.k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              sortBy === o.k ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            {o.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">Cargando...</div>
      ) : sorted.length === 0 ? (
        <div className="py-12 text-center text-gray-400">Aún no hay datos de uso registrados</div>
      ) : (
        <div className="space-y-2">
          {sorted.map((u) => (
            <div key={u.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`usage-row-${u.id}`}>
              <button
                onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                data-testid={`usage-row-toggle-${u.id}`}
              >
                {/* Avatar */}
                {u.profile_image ? (
                  <img src={u.profile_image} alt={u.full_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-gray-500 font-bold">
                    {(u.full_name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-black truncate">{u.full_name}</p>
                    {u.is_admin && <span className="text-[9px] bg-black text-white px-1.5 py-0.5 rounded">ADMIN</span>}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                </div>
                {/* Stats */}
                <div className="hidden sm:flex items-center gap-4 text-center flex-shrink-0">
                  <div>
                    <div className="text-sm font-bold text-black flex items-center gap-1"><Clock size={13} className="text-gray-400" />{formatDuration(u.total_minutes)}</div>
                    <div className="text-[9px] text-gray-400">Tiempo</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-black flex items-center gap-1"><CalendarDays size={13} className="text-gray-400" />{u.active_days}</div>
                    <div className="text-[9px] text-gray-400">Días</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-black flex items-center gap-1"><Trophy size={13} className="text-amber-500" />{u.total_points.toLocaleString()}</div>
                    <div className="text-[9px] text-gray-400">Puntos</div>
                  </div>
                  <div className="w-28">
                    <div className="text-xs font-semibold text-gray-700 flex items-center gap-1 justify-center"><Award size={12} className="text-gray-400" />{u.rank_name}</div>
                    <div className="text-[9px] text-gray-400">Rango</div>
                  </div>
                </div>
                {expandedId === u.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>
              {/* Mobile stats */}
              <div className="sm:hidden flex items-center justify-around px-3 pb-2 text-center border-t border-gray-100 pt-2">
                <div><div className="text-xs font-bold">{formatDuration(u.total_minutes)}</div><div className="text-[9px] text-gray-400">Tiempo</div></div>
                <div><div className="text-xs font-bold">{u.active_days}</div><div className="text-[9px] text-gray-400">Días</div></div>
                <div><div className="text-xs font-bold">{u.total_points.toLocaleString()}</div><div className="text-[9px] text-gray-400">Puntos</div></div>
                <div><div className="text-[10px] font-semibold">{u.rank_name}</div><div className="text-[9px] text-gray-400">Rango</div></div>
              </div>
              {expandedId === u.id && (
                <div className="px-3 pb-3">
                  <UsageDetail apiClient={apiClient} userId={u.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserUsageAdmin;
